import { MasterChecklistEditor } from './MasterChecklistEditor';
import { SupplierCatalog } from './SupplierCatalog';
import { useState } from 'react';
import type { SpaceKind } from '@sivan/shared';
import { chooseProject, useCurrentProject } from '../current-project';
import { ApartmentSheet } from './ApartmentSheet';
import { HandoverSettings } from './HandoverSettings';
import { ProjectAdmin } from './ProjectAdmin';
import { SpaceSheet } from './SpaceSheet';
import type { PastePermissions } from './apartment-paste';
import { UnitsWorkspace } from './UnitsWorkspace';
import { Problem } from '../handover/common';
import { confirmDiscardPending } from './unsaved-changes';
import {
  projectPermissions,
  useApartmentSheet,
  useSpaceSheet,
  useProjectData,
  useWorkspace,
} from './use-master-data';

function LoadingRows() {
  return (
    <div className="skeleton" role="status" aria-label="טוען נתונים">
      {Array.from({ length: 8 }, (_, i) => (
        <div key={i} className="skeleton__row" />
      ))}
    </div>
  );
}

const NO_ACCESS = 'אין לך הרשאה לצפות במידע זה. לבירור פנו למנהלי המערכת.';

function ProjectUnits({
  projectId,
  canEditStored,
  canCreate,
}: {
  readonly projectId: string;
  readonly canEditStored: boolean;
  readonly canCreate: boolean;
}) {
  const [version, setVersion] = useState(0);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);
  const [data, reload] = useProjectData(projectId, version);
  if (data.state === 'loading') return <LoadingRows />;
  if (data.state === 'forbidden') return <Problem text={NO_ACCESS} />;
  if (data.state === 'error')
    return <Problem text="טעינת היחידות נכשלה." requestId={data.requestId} onRetry={reload} />;
  if (data.data.structures.length === 0)
    return <p className="empty">עדיין לא הוגדרו בניינים בפרויקט.</p>;
  return (
    <UnitsWorkspace
      key={`${projectId}:${String(version)}`}
      projectId={projectId}
      data={data.data}
      canEditStored={canEditStored}
      canCreate={canCreate}
      onSaved={(message) => {
        setSavedMessage(message);
        setVersion((v) => v + 1);
      }}
      savedMessage={savedMessage}
    />
  );
}

function ProjectApartments({
  projectId,
  permissions,
}: {
  readonly projectId: string;
  readonly permissions: PastePermissions;
}) {
  const [version, setVersion] = useState(0);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);
  const [data, reload] = useApartmentSheet(projectId, version);
  if (data.state === 'loading') return <LoadingRows />;
  if (data.state === 'forbidden') return <Problem text={NO_ACCESS} />;
  if (data.state === 'error')
    return (
      <Problem text="טעינת גיליון הדירות נכשלה." requestId={data.requestId} onRetry={reload} />
    );
  if (data.data.structures.length === 0)
    return <p className="empty">עדיין לא הוגדרו בניינים בפרויקט.</p>;
  return (
    <ApartmentSheet
      key={`${projectId}:${String(version)}`}
      projectId={projectId}
      data={data.data}
      permissions={permissions}
      onSaved={(message) => {
        setSavedMessage(message);
        setVersion((v) => v + 1);
      }}
      savedMessage={savedMessage}
    />
  );
}

/**
 * The Apartments sheet is the Master Data workspace. The other views work on the same records and
 * stay available as secondary administrative views (structure quality control, bulk space work).
 */
const SHEETS = [
  { id: 'apartments', label: 'דירות' },
  { id: 'parking', label: 'חניות' },
  { id: 'storage', label: 'מחסנים' },
  { id: 'units', label: 'כל היחידות' },
  // Project catalogs for handover findings (H-2); not part of the apartment sheet.
  { id: 'handover-settings', label: 'הגדרות מסירות' },
] as const;
const ADMIN_VIEWS = SHEETS.filter((s) => s.id !== 'apartments');

function ProjectSpaces({
  projectId,
  kind,
  permissions,
}: {
  readonly projectId: string;
  readonly kind: SpaceKind;
  readonly permissions: PastePermissions;
}) {
  const [version, setVersion] = useState(0);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);
  const [data, reload] = useSpaceSheet(projectId, kind, version);
  if (data.state === 'loading') return <LoadingRows />;
  if (data.state === 'forbidden') return <Problem text={NO_ACCESS} />;
  if (data.state === 'error')
    return <Problem text="טעינת הגיליון נכשלה." requestId={data.requestId} onRetry={reload} />;
  return (
    <SpaceSheet
      key={`${projectId}:${kind}:${String(version)}`}
      projectId={projectId}
      kind={kind}
      data={data.data}
      permissions={permissions}
      onSaved={(message) => {
        setSavedMessage(message);
        setVersion((v) => v + 1);
      }}
      savedMessage={savedMessage}
    />
  );
}
type SheetId = (typeof SHEETS)[number]['id'];

/** ניהול נתוני אב → פרויקט → גיליון (synthetic development data only). */
export function MasterDataScreen() {
  const [loaded, reload] = useWorkspace();
  const chosen = useCurrentProject(loaded.state === 'ready' ? loaded.data.projects : []);
  const [sheet, setSheet] = useState<SheetId>('apartments');
  // ניהול פרויקטים (PROJECTS-1): outside any one project's sheets.
  const [managing, setManaging] = useState(false);
  // The company checklist (DR-195), from the projects screen.
  const [company, setCompany] = useState<'checklist' | 'suppliers' | null>(null);
  // A reload (a project was added or renamed) keeps the screen, and what is open in it.
  const [last, setLast] = useState<typeof loaded | null>(null);
  if (loaded.state === 'ready' && last !== loaded) setLast(loaded);
  const workspace = loaded.state === 'loading' && last?.state === 'ready' ? last : loaded;

  if (workspace.state === 'loading') return <LoadingRows />;
  if (workspace.state === 'forbidden') return <Problem text={NO_ACCESS} />;
  if (workspace.state === 'error')
    return (
      <Problem text="טעינת נתוני האב נכשלה." requestId={workspace.requestId} onRetry={reload} />
    );

  const { me, projects } = workspace.data;
  const ADMIN_KEYS = ['projects.project.edit', 'projects.structure.edit'];
  const canManage =
    me.access.organization.includes('organization.project.create') ||
    me.access.allProjects.some((k) => ADMIN_KEYS.includes(k)) ||
    me.access.projects.some((p) => p.permissions.some((k) => ADMIN_KEYS.includes(k)));
  if (canManage && (managing || projects.length === 0))
    return (
      <div className="master-data">
        <nav aria-label="מיקום" className="breadcrumbs">
          <ol>
            <li>ניהול נתוני אב</li>
            <li aria-current="page">ניהול פרויקטים</li>
          </ol>
        </nav>
        <div className="project-bar">
          {projects.length > 0 && (
            <button
              type="button"
              className="button button--small"
              onClick={() => {
                setManaging(false);
              }}
            >
              חזרה לגיליונות הפרויקט
            </button>
          )}
          {(
            [
              ['checklist', 'רשימת הבדיקה של סיון', 'organization.project.create'],
              ['suppliers', 'ספקים וקבלנים', 'organization.supplier.manage'],
            ] as const
          ).map(([view, label, key]) =>
            me.access.organization.includes(key) ? (
              <button
                key={view}
                type="button"
                className="button button--small"
                aria-pressed={company === view}
                onClick={() => {
                  void confirmDiscardPending().then((ok) => {
                    if (ok) setCompany((v) => (v === view ? null : view));
                  });
                }}
              >
                {company === view ? 'חזרה לפרויקטים' : label}
              </button>
            ) : null,
          )}
        </div>
        {company === 'checklist' ? (
          <MasterChecklistEditor />
        ) : company === 'suppliers' ? (
          <SupplierCatalog />
        ) : (
          <ProjectAdmin
            onWorkspaceChanged={reload}
            onOpenSheet={(projectId) => {
              chooseProject(projectId);
              setSheet('apartments');
              setManaging(false);
            }}
          />
        )}
      </div>
    );
  if (projects.length === 0) return <p className="empty">אין פרויקטים זמינים עבורך.</p>;
  const project = chosen ?? projects[0];
  if (project === undefined) return null;
  const permissions = projectPermissions(me, project.id);
  const sheetPermissions: PastePermissions = {
    create: permissions.has('projects.unit.create'),
    edit: permissions.has('projects.unit.edit'),
    allocate: permissions.has('projects.allocation.edit'),
  };
  const readOnly = !sheetPermissions.create && !sheetPermissions.edit && !sheetPermissions.allocate;
  const current = SHEETS.find((s) => s.id === sheet) ?? SHEETS[0];
  const choose = (id: SheetId) => {
    if (id !== sheet)
      void confirmDiscardPending().then((ok) => {
        if (ok) setSheet(id);
      });
  };
  return (
    <div className="master-data">
      {/* The development warning (DR-124) is the persistent badge in the application header. */}
      <nav aria-label="מיקום" className="breadcrumbs">
        <ol>
          <li>ניהול נתוני אב</li>
          <li>{project.name}</li>
          <li aria-current="page">{current.label}</li>
        </ol>
      </nav>
      <div className="project-bar">
        <span className="hint">
          פרויקט: <b>{project.name}</b> (<bdi dir="ltr">{project.code}</bdi>)
        </span>
        {canManage && (
          <button
            type="button"
            className="button button--small"
            onClick={() => {
              void confirmDiscardPending().then((ok) => {
                if (ok) setManaging(true);
              });
            }}
          >
            ניהול פרויקטים
          </button>
        )}
        {readOnly && <span className="chip chip--stored">צפייה בלבד</span>}
      </div>
      {sheet === 'apartments' ? (
        <details className="admin-views">
          <summary>תצוגות ניהול</summary>
          <p className="hint">
            אותם נתונים, בתצוגה לפי מקום או יחידה (לבקרת איכות ולעבודה מרוכזת).
          </p>
          <ul>
            {ADMIN_VIEWS.map((view) => (
              <li key={view.id}>
                <button
                  type="button"
                  className="button button--small button--ghost"
                  onClick={() => {
                    choose(view.id);
                  }}
                >
                  {view.label}
                </button>
              </li>
            ))}
          </ul>
        </details>
      ) : (
        <div className="admin-views admin-views--active">
          <span className="chip chip--stored">תצוגת ניהול: {current.label}</span>
          <button
            type="button"
            className="button button--small"
            onClick={() => {
              choose('apartments');
            }}
          >
            חזרה לגיליון הדירות
          </button>
        </div>
      )}
      <div className="sheet-panel">
        {sheet === 'apartments' ? (
          <ProjectApartments
            key={project.id}
            projectId={project.id}
            permissions={sheetPermissions}
          />
        ) : sheet === 'handover-settings' ? (
          <HandoverSettings key={project.id} projectId={project.id} />
        ) : sheet === 'units' ? (
          <ProjectUnits
            key={project.id}
            projectId={project.id}
            canEditStored={sheetPermissions.edit}
            canCreate={sheetPermissions.create}
          />
        ) : (
          <ProjectSpaces
            key={`${project.id}:${sheet}`}
            projectId={project.id}
            kind={sheet}
            permissions={sheetPermissions}
          />
        )}
      </div>
    </div>
  );
}
